import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  Menu,
  Edit,
  People,
  LocalHospital,
  CalendarToday,
  Schedule,
  Person,
  SwapHoriz,
  ExitToApp
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const { logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = () => {
    logout();
    navigate('/home');
    onClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleExpanded = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setExpanded(!expanded);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const drawerWidth = isMobile ? 250 : (expanded ? 250 : 70);

  // Menu items for nursing staff (users) - wensenlijst, overzicht, persoonlijk overzicht en dag wisseling
  const userMenuItems = [
    { icon: <Edit />, text: 'verpl. wensenlijsten', path: '/wensenlijsten' },
    { icon: <CalendarToday />, text: 'Toeren overzicht', path: '/overzicht' },
    { icon: <Person />, text: 'pers. overzicht', path: '/persoonlijk-overzicht' },
  ];

  // Menu items for administrators - full access
  const adminMenuItems = [
    { icon: <Edit />, text: 'verpl. wensenlijsten', path: '/wensenlijsten' },
    { icon: <People />, text: 'verpleegkundigen', path: '/verpleegkundigen' },
    { icon: <LocalHospital />, text: 'rondes', path: '/rondes' },
    { icon: <CalendarToday />, text: 'verpl. overzicht', path: '/overzicht' },
    { icon: <Schedule />, text: 'uurrooster', path: '/uurrooster' }
  ];

  // Show different menu items based on user role
  const role = user?.role?.toLowerCase();

    const menuItems =
      role === 'admin'
        ? adminMenuItems
        : userMenuItems; // nurse krijgt deze standaard


  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Menu Toggle Button */}
      <ListItem 
        button 
        onClick={toggleExpanded}
        sx={{
          py: 3,
          px: isMobile || expanded ? 2 : 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          justifyContent: isMobile || expanded ? 'flex-start' : 'center',
          '&:hover': { 
            backgroundColor: 'rgba(255,255,255,0.1)'
          }
        }}
      >
        <ListItemIcon sx={{ 
          color: 'white', 
          minWidth: isMobile || expanded ? 40 : 'auto',
          justifyContent: 'center'
        }}>
          <Menu />
        </ListItemIcon>
        {(isMobile || expanded) && (
          <ListItemText 
            primary="menu"
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: 400
            }}
          />
        )}
      </ListItem>

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, py: 0 }}>
        {menuItems.map((item, index) => (
          <ListItem 
            key={index}
            button 
            onClick={() => handleNavigate(item.path)}
            sx={{ 
              py: 3,
              px: isMobile || expanded ? 2 : 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              justifyContent: isMobile || expanded ? 'flex-start' : 'center',
              '&:hover': { 
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: 'white', 
              minWidth: isMobile || expanded ? 40 : 'auto',
              justifyContent: 'center'
            }}>
              {item.icon}
            </ListItemIcon>
            {(isMobile || expanded) && (
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: 400
                }}
              />
            )}
          </ListItem>
        ))}
      </List>

      {/* Uitloggen Button at Bottom */}
      <Box sx={{ 
        mt: 'auto',
        borderTop: '1px solid rgba(255,255,255,0.1)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{ 
            py: 3,
            px: isMobile || expanded ? 2 : 1.5,
            justifyContent: isMobile || expanded ? 'flex-start' : 'center',
            '&:hover': { 
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <ListItemIcon sx={{ 
            color: 'white', 
            minWidth: isMobile || expanded ? 40 : 'auto',
            justifyContent: 'center'
          }}>
            <ExitToApp />
          </ListItemIcon>
          {(isMobile || expanded) && (
            <ListItemText 
              primary="uitloggen"
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 400
              }}
            />
          )}
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          sx={{ 
            backgroundColor: '#4aa8b4', 
            zIndex: theme.zIndex.drawer + 1 
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleExpanded}
              sx={{ mr: 2 }}
            >
              <Menu />
            </IconButton>
            <Typography variant="h6" noWrap>
              Thuisverpleging Portaal
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: '#4aa8b4',
              color: 'white',
              border: 'none',
              transition: 'width 0.3s ease',
              overflow: 'hidden'
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              backgroundColor: '#4aa8b4',
              color: 'white',
              border: 'none'
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
